import { ARTIST_PORTAL_ROUTE } from 'config'

const STORAGE_KEY_PREFIX = 'artistProfileCompletion:'

export const SECTION_KEYS = {
  ACCOUNT: 'account_details',
  EXPERIENCE_SKILLS: 'experience_skills',
  PROGRAM_DELIVERY: 'program_delivery',
}

export const SECTION_META = {
  [SECTION_KEYS.ACCOUNT]: {
    label: 'Account Details',
    tooltip:
      'All fields with an asterisk are required to complete this section.',
    route: ARTIST_PORTAL_ROUTE + '/profile/account',
  },
  [SECTION_KEYS.EXPERIENCE_SKILLS]: {
    label: 'Experience & Skills',
    tooltip:
      'Education, Experience, and Skills & Offerings are required to complete this section.',
    route: ARTIST_PORTAL_ROUTE + '/profile/experience-skills',
  },
  [SECTION_KEYS.PROGRAM_DELIVERY]: {
    label: 'Program Delivery',
    tooltip:
      'Add at least one program or partnership for the current school year to complete this section.',
    route: ARTIST_PORTAL_ROUTE + '/profile/program-delivery',
  },
}

export function getSections() {
  return [
    SECTION_KEYS.ACCOUNT,
    SECTION_KEYS.EXPERIENCE_SKILLS,
    SECTION_KEYS.PROGRAM_DELIVERY,
  ]
}

function hasEducationComplete(credentials) {
  if (!Array.isArray(credentials)) return false
  return credentials.some(
    (credential) =>
      credential.credentialType === 'degree' &&
      credential.institutionName &&
      credential.credentialName
  )
}

function hasExperienceComplete(experiences) {
  if (!Array.isArray(experiences)) return false
  return experiences.some(
    (experience) =>
      experience.title &&
      experience.ftPtCo &&
      experience.organizationName &&
      experience.fullLocation &&
      experience.startDate &&
      experience.endDate
  )
}

function hasSkillsAndOfferingsComplete(skills) {
  const skillGroups = skills || {}
  return [
    skillGroups.approaches,
    skillGroups.outcomes,
    skillGroups.languages,
    skillGroups.subDisciplines,
    skillGroups.programTypes,
  ].some((arr) => Array.isArray(arr) && arr.length > 0)
}

export function computePercent(completedCount, totalCount) {
  if (!totalCount) return 0
  if (completedCount <= 0) return 0
  return Math.round((completedCount / totalCount) * 100)
}

function belongsToSchoolYear(record, schoolYearId) {
  if (schoolYearId == null || !record) return false
  return Number(record.schoolYearId) === Number(schoolYearId)
}

function hasCurrentYearProgramDelivery(artist, currentSchoolYearId) {
  if (currentSchoolYearId == null) return false

  const partnerships = Array.isArray(artist.partnerships)
    ? artist.partnerships
    : []
  const taPrograms = Array.isArray(artist.taPrograms) ? artist.taPrograms : []
  const isCurrentYearRecord = (record) =>
    !record._destroy && belongsToSchoolYear(record, currentSchoolYearId)

  return (
    partnerships.some(isCurrentYearRecord) ||
    taPrograms.some(isCurrentYearRecord)
  )
}

export function isSectionComplete(sectionKey, artist, options = {}) {
  if (!artist) return false

  switch (sectionKey) {
    case SECTION_KEYS.ACCOUNT: {
      const profile = artist.profile || {}
      const detail = artist.detail || {}
      const address = artist.address || {}
      const hasYearsOfExperience =
        profile.yearsOfExperience !== null &&
        profile.yearsOfExperience !== undefined &&
        profile.yearsOfExperience !== ''

      return Boolean(
        profile.firstName &&
          profile.lastName &&
          detail.phone &&
          artist.profilePic &&
          address.street &&
          address.city &&
          address.state &&
          address.zip &&
          hasYearsOfExperience &&
          artist.description
      )
    }
    case SECTION_KEYS.EXPERIENCE_SKILLS:
      return (
        hasEducationComplete(artist.credentials) &&
        hasExperienceComplete(artist.experiences) &&
        hasSkillsAndOfferingsComplete(artist.skills)
      )
    case SECTION_KEYS.PROGRAM_DELIVERY:
      return hasCurrentYearProgramDelivery(artist, options.currentSchoolYearId)
    default:
      return false
  }
}

export function deriveCompletedSections(artist, options = {}) {
  return getSections().filter((sectionKey) =>
    isSectionComplete(sectionKey, artist, options)
  )
}

function storageKey(artistId) {
  return `${STORAGE_KEY_PREFIX}${artistId}`
}

export function setStoredProfileCompletion(artistId, status) {
  if (!artistId || typeof window === 'undefined') return
  window.localStorage.setItem(storageKey(artistId), JSON.stringify(status))
}

export function buildBarModel(artist, options = {}) {
  const sections = getSections()
  const completedSections = deriveCompletedSections(artist, options)
  const completedSet = new Set(completedSections)
  const orderedCompletedSections = sections.filter((sectionKey) =>
    completedSet.has(sectionKey)
  )
  const orderedIncompleteSections = sections.filter(
    (sectionKey) => !completedSet.has(sectionKey)
  )
  const orderedSections = [
    ...orderedCompletedSections,
    ...orderedIncompleteSections,
  ]

  return {
    sections: orderedSections.map((key) => ({ key, ...SECTION_META[key] })),
    completedSections: orderedCompletedSections,
    percent: computePercent(completedSections.length, sections.length),
    totalCount: sections.length,
  }
}
